
import { useState } from 'react';
import { Info, Settings2, Sparkles } from 'lucide-react';
import { type PowerInfo } from '@/lib/powers-data';
import {
  EVERYPOWER_PSTYLE_VARIANTS,
  type EverypowerPstyleVariantId,
  pstyleAnimatedGradientStyle,
} from '@/lib/power-pstyle-themes';
import { GallerySectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import PowerProfileDialogMock, {
  XAT_PROFILE_DIALOG_WIDTH,
} from '@/components/help-center/powers/PowerProfileDialogMock';
import { cn } from '@/components/help-center/latest-power-shared';

interface PowerEverypowerPstyleSectionProps {
  power: PowerInfo;
}

export default function PowerEverypowerPstyleSection({ power }: PowerEverypowerPstyleSectionProps) {
  const [activeVariant, setActiveVariant] = useState<EverypowerPstyleVariantId>('emerald-fx');

  if (power.slug !== 'everypower') return null;

  const variant =
    EVERYPOWER_PSTYLE_VARIANTS.find((item) => item.id === activeVariant) ??
    EVERYPOWER_PSTYLE_VARIANTS[0];

  return (
    <section className="border-b border-gray-100 bg-white py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-start">
          <div className="min-w-0">
            <div className="mb-8 flex items-center gap-3">
              <GallerySectionSmileyIcon code="pstyle" bgClass="bg-emerald-50" />
              <div>
                <h2 className="font-poppins text-xl font-bold text-gray-900">
                  Temas Pstyle bónus
                </h2>
                <p className="mt-0.5 font-inter text-sm text-emerald-700">
                  Pstyle + EveryPower activo
                </p>
              </div>
            </div>

            <div className="space-y-4 font-inter text-base leading-relaxed text-gray-600">
              <p>
                O <strong className="font-semibold text-gray-800">EveryPower</strong> é o único
                requisito especial que desbloqueia <strong className="font-semibold text-gray-800">dois</strong>{' '}
                temas bónus animados no separador Pstyle:{' '}
                <strong className="font-semibold text-gray-800">Emerald Fx</strong> e{' '}
                <strong className="font-semibold text-gray-800">Supernova Fx</strong>. Ambos usam
                degradês em movimento contínuo no header, caixas de informação e botões do diálogo de
                perfil.
              </p>
              <p>
                É necessário ter o power <strong className="font-semibold text-gray-800">Pstyle</strong>{' '}
                activo e o EveryPower ligado. Escolha o tema no separador Pstyle das configurações —
                a pré-visualização abaixo replica as cores e a animação do xat.
              </p>
            </div>

            <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
              <Settings2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              <p className="font-inter text-sm leading-relaxed text-emerald-900">
                Configurações → <strong>Pstyle</strong> → tema bónus{' '}
                <strong>Emerald Fx</strong> ou <strong>Supernova Fx</strong>. Use{' '}
                <strong>Pré-visualizar</strong> antes de guardar. O Pstyle não aparece no Modo Noite.
              </p>
            </div>

            <div className="mt-8">
              <div className="mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-emerald-600" aria-hidden />
                <h3 className="font-poppins text-sm font-semibold text-gray-900">
                  Escolha o tema para pré-visualizar
                </h3>
              </div>

              <div
                className="inline-flex w-full max-w-md flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50/80 p-1.5 shadow-sm sm:flex-row"
                role="tablist"
                aria-label="Temas Pstyle do EveryPower"
              >
                {EVERYPOWER_PSTYLE_VARIANTS.map((item) => {
                  const selected = item.id === activeVariant;
                  const previewGradient =
                    item.mock.animatedGradient ?? item.mock.headerGradient;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setActiveVariant(item.id)}
                      className={cn(
                        'flex flex-1 items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-all',
                        selected
                          ? 'bg-white text-gray-900 shadow-sm ring-1 ring-emerald-200'
                          : 'text-gray-600 hover:bg-white/70 hover:text-gray-900'
                      )}
                    >
                      <span
                        className="h-9 w-9 shrink-0 rounded-md ring-1 ring-black/10"
                        style={
                          item.mock.animatedGradient
                            ? pstyleAnimatedGradientStyle(item.mock.animatedGradient)
                            : { background: previewGradient }
                        }
                        aria-hidden
                      />
                      <span className="min-w-0">
                        <span className="block font-poppins text-sm font-semibold leading-tight">
                          {item.themeName}
                        </span>
                        <span className="block font-inter text-[11px] text-gray-500">
                          Degradê animado
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex min-w-0 justify-center">
            <div
              className="flex w-full flex-col items-center"
              style={{ maxWidth: XAT_PROFILE_DIALOG_WIDTH }}
            >
              <div className="mb-3 flex w-full items-center justify-center gap-2">
                <Info className="h-4 w-4 text-gray-400" aria-hidden />
                <p className="font-poppins text-xs font-semibold uppercase tracking-widest text-gray-400">
                  {variant.themeName}
                </p>
              </div>

              <PowerProfileDialogMock key={variant.id} mock={variant.mock} className="w-full" />

              <p className="mt-4 w-full text-center font-inter text-[11px] leading-relaxed text-gray-400">
                Animação de 15s em loop — igual ao efeito{' '}
                <code className="rounded bg-gray-100 px-1 py-0.5 text-[10px]">.pstyleFx</code> do
                xat.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
